import React from 'react';
import { Shield, Zap, TrendingUp, Check, X } from 'lucide-react';

const Engagement = () => {
  const retainerPillars = [
    {
      icon: Shield,
      title: 'API Resilience',
      description: 'Monitoring and updating the "pipes" as Square, QuickBooks, or Hudu update their data schemas to prevent downtime.',
      details: [
        'Real-time API health monitoring',
        'Schema change detection and updates',
        'Zero-downtime migration support',
        'Proactive vendor compatibility tracking'
      ]
    },
    {
      icon: Zap,
      title: 'Logic Tuning',
      description: 'Refining AI synthesis prompts and data science math to ensure answers remain accurate as your business data grows.',
      details: [
        'Prompt optimization for accuracy',
        'Business logic refinement',
        'Context window management',
        'Performance tuning as data scales'
      ]
    },
    {
      icon: TrendingUp,
      title: 'Ongoing Orchestration',
      description: 'Scaling the Intelligence Layer by adding new data sources and answering new business questions as they arise.',
      details: [
        'New integration deployment',
        'Custom query development',
        'Data source expansion',
        'Business intelligence evolution'
      ]
    }
  ];

  const comparisonData = [
    {
      feature: 'Data Ownership',
      gscs: 'You own 100% of your data',
      saas: 'Locked in a third-party cloud'
    },
    {
      feature: 'Privacy',
      gscs: 'Private infrastructure under your control',
      saas: 'Data used for model training'
    },
    {
      feature: 'AI Model Choice',
      gscs: 'Your choice: Gemini, ChatGPT, Claude, or private models',
      saas: 'Vendor-locked to their model'
    },
    {
      feature: 'Flexibility',
      gscs: 'Bespoke logic tailored to your business',
      saas: 'One-size-fits-all features'
    },
    {
      feature: 'Integration',
      gscs: 'Connect to any system you use',
      saas: 'Limited to pre-built connectors'
    },
    {
      feature: 'Cost Structure',
      gscs: 'Build once, maintain transparently',
      saas: 'Perpetual subscription per user'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Intelligence is an Evolution, Not a Project.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Your business doesn't stand still. Neither should your Intelligence Layer.
            Professional maintenance ensures your AI Business Brain stays sharp, accurate, and aligned with your evolving operations.
          </p>
        </div>

        {/* Retainer Pillars */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Why Ongoing Maintenance is Essential
          </h3>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Build once, maintain always. Here's what our retainer delivers:
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {retainerPillars.map((pillar, index) => {
              const IconComponent = pillar.icon;
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:border-primary-300"
                >
                  <div className="bg-primary-100 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="h-8 w-8 text-primary-600" />
                  </div>

                  <h4 className="text-2xl font-bold text-gray-900 mb-4">
                    {pillar.title}
                  </h4>

                  <p className="text-gray-600 leading-relaxed mb-6">
                    {pillar.description}
                  </p>

                  <ul className="space-y-3">
                    {pillar.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start text-gray-700">
                        <Check className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sovereign vs SaaS Comparison */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Sovereign AI vs. Traditional SaaS
          </h3>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Not all AI solutions are created equal. Here's how our model compares:
          </p>

          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
              <div className="p-6 font-semibold text-lg">
                Feature
              </div>
              <div className="p-6 font-semibold text-lg border-l border-primary-500">
                GSCS Sovereign AI
              </div>
              <div className="p-6 font-semibold text-lg border-l border-primary-500">
                Traditional SaaS
              </div>
            </div>

            {/* Table Body */}
            {comparisonData.map((row, index) => (
              <div
                key={index}
                className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
              >
                <div className="p-6 font-semibold text-gray-900 flex items-center">
                  {row.feature}
                </div>
                <div className="p-6 border-l border-gray-200 flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">{row.gscs}</span>
                </div>
                <div className="p-6 border-l border-gray-200 flex items-center">
                  <X className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">{row.saas}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Trust Statement */}
          <div className="mt-8 text-center">
            <p className="text-lg text-gray-700 font-medium">
              We build the pipes; you hold the keys.
            </p>
          </div>
        </div>

        {/* Business Model Clarification */}
        <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl p-10 border border-primary-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Our Business Model: Transparent & Aligned
          </h3>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-primary-600 font-bold text-lg mb-3">Build & Integrate</div>
              <p className="text-gray-700 leading-relaxed">
                One-time engineering fee to design, build, and deploy your Intelligence Layer.
                You own the code, the infrastructure, and the data.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-primary-600 font-bold text-lg mb-3">Monthly Maintenance</div>
              <p className="text-gray-700 leading-relaxed">
                Ongoing retainer for API monitoring, logic tuning, and orchestration updates.
                Your Intelligence Layer evolves with your business and the industry.
              </p>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm italic">
              No per-user fees. No data mining. No vendor lock-in.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Engagement;
